/**
 * classifyDdlAtCursor — pure DDL keyword classifier used by the
 * `useDdlStatementDetection` hook.
 *
 * Resolver-classification only: re-uses `tryConsumeKeyword` /
 * `skipWsCommentsLocal` from `sql-resolver/ddl/extract/shared` so the scan is
 * comment-aware and handles MySQL `#` comments correctly. No regex (per
 * VQE-D10).
 *
 * Coverage matches PRD `Ralph/VqeDdlEdit/PRD.md` §Slice 0.3 acceptance:
 *   CREATE TABLE / ALTER TABLE
 *   CREATE [MATERIALIZED] VIEW / ALTER VIEW
 *   CREATE / ALTER SEQUENCE
 *   CREATE TYPE / CREATE DOMAIN
 *   CREATE SCHEMA
 *   CREATE TRIGGER
 *   CREATE PROCEDURE / CREATE FUNCTION
 *   CREATE LOGIN / CREATE USER / CREATE ROLE
 *   GRANT / REVOKE
 *   CREATE / ALTER DATABASE
 *   CREATE [UNIQUE] INDEX
 *   top-level DROP …
 *
 * Per-engine modifiers (TEMP, TEMPORARY, GLOBAL, LOCAL, UNLOGGED, OR REPLACE,
 * IF NOT EXISTS, …) are skipped before the kind keyword.
 */

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import {
  skipWsCommentsLocal,
  tryConsumeKeyword,
} from '../../../sql-resolver/ddl/extract/shared';
import type { DdlObjectKind } from '../edit/editContext';

export type DdlKind =
  | 'create-table'
  | 'alter-table'
  | 'create-view'
  | 'alter-view'
  | 'create-materialized-view'
  | 'alter-materialized-view'
  | 'create-sequence'
  | 'alter-sequence'
  | 'create-type'
  | 'create-domain'
  | 'create-schema'
  | 'create-trigger'
  | 'alter-trigger'
  | 'create-procedure'
  | 'alter-procedure'
  | 'create-function'
  | 'alter-function'
  | 'create-package'
  | 'create-package-body'
  | 'alter-package'
  | 'create-login'
  | 'alter-login'
  | 'create-user'
  | 'alter-user'
  | 'create-role'
  | 'alter-role'
  | 'grant'
  | 'revoke'
  | 'create-database'
  | 'alter-database'
  | 'create-index'
  | 'drop';

export type DdlClassification = {
  readonly ddlKind: DdlKind;
  readonly objectKind: DdlObjectKind;
};

const PRE_KIND_MODIFIERS: ReadonlySet<string> = new Set([
  'TEMP', 'TEMPORARY', 'GLOBAL', 'LOCAL', 'UNLOGGED', 'VOLATILE', 'PRIVATE',
  'SHARDED', 'DUPLICATED',
  'UNIQUE', 'CLUSTERED', 'NONCLUSTERED', 'BITMAP', 'FULLTEXT', 'SPATIAL',
  'MATERIALIZED',
  'OR', 'REPLACE', 'EDITIONABLE', 'NONEDITIONABLE',
  'DEFINER', 'INVOKER',
  'CONSTRAINT',
]);

/** Skip a chain of leading modifiers (bounded loop so degenerate input cannot
 *  spin forever). Returns the first non-modifier offset and a boolean
 *  signalling whether `MATERIALIZED` appeared (CREATE MATERIALIZED VIEW). */
function consumeModifiers(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): { offset: number; sawMaterialized: boolean } {
  let i = start;
  let sawMaterialized = false;
  for (let iter = 0; iter < 8; iter++) {
    const ws = skipWsCommentsLocal(source, i, end, options);
    let advanced = ws;
    let matchedMaterialized = false;
    for (const modifier of PRE_KIND_MODIFIERS) {
      const next = tryConsumeKeyword(source, ws, end, options, modifier);
      if (next !== ws) {
        advanced = next;
        if (modifier === 'MATERIALIZED') matchedMaterialized = true;
        break;
      }
    }
    if (advanced === ws) return { offset: i, sawMaterialized };
    if (matchedMaterialized) sawMaterialized = true;
    i = advanced;
  }
  return { offset: i, sawMaterialized };
}

const CREATE_KIND_TO_OBJECT_KIND: Readonly<Record<string, { ddlKind: DdlKind; objectKind: DdlObjectKind }>> = {
  TABLE: { ddlKind: 'create-table', objectKind: 'table' },
  VIEW: { ddlKind: 'create-view', objectKind: 'view' },
  SEQUENCE: { ddlKind: 'create-sequence', objectKind: 'sequence' },
  TYPE: { ddlKind: 'create-type', objectKind: 'type' },
  DOMAIN: { ddlKind: 'create-domain', objectKind: 'domain' },
  SCHEMA: { ddlKind: 'create-schema', objectKind: 'schema' },
  TRIGGER: { ddlKind: 'create-trigger', objectKind: 'trigger' },
  PROCEDURE: { ddlKind: 'create-procedure', objectKind: 'routine' },
  PROC: { ddlKind: 'create-procedure', objectKind: 'routine' },
  FUNCTION: { ddlKind: 'create-function', objectKind: 'routine' },
  PACKAGE: { ddlKind: 'create-package', objectKind: 'package' },
  LOGIN: { ddlKind: 'create-login', objectKind: 'security' },
  USER: { ddlKind: 'create-user', objectKind: 'security' },
  ROLE: { ddlKind: 'create-role', objectKind: 'security' },
  DATABASE: { ddlKind: 'create-database', objectKind: 'database' },
  INDEX: { ddlKind: 'create-index', objectKind: 'index' },
};

const ALTER_KIND_TO_OBJECT_KIND: Readonly<Record<string, { ddlKind: DdlKind; objectKind: DdlObjectKind }>> = {
  TABLE: { ddlKind: 'alter-table', objectKind: 'table' },
  VIEW: { ddlKind: 'alter-view', objectKind: 'view' },
  SEQUENCE: { ddlKind: 'alter-sequence', objectKind: 'sequence' },
  TRIGGER: { ddlKind: 'alter-trigger', objectKind: 'trigger' },
  PROCEDURE: { ddlKind: 'alter-procedure', objectKind: 'routine' },
  PROC: { ddlKind: 'alter-procedure', objectKind: 'routine' },
  FUNCTION: { ddlKind: 'alter-function', objectKind: 'routine' },
  PACKAGE: { ddlKind: 'alter-package', objectKind: 'package' },
  LOGIN: { ddlKind: 'alter-login', objectKind: 'security' },
  USER: { ddlKind: 'alter-user', objectKind: 'security' },
  ROLE: { ddlKind: 'alter-role', objectKind: 'security' },
  DATABASE: { ddlKind: 'alter-database', objectKind: 'database' },
};

/**
 * Classify a statement's DDL form by scanning leading keywords. Returns
 * `null` when the statement is not a recognised DDL form (e.g. SELECT, or
 * an unsupported DDL flavour the visual editor cannot host yet).
 */
export function classifyDdlAtCursor(
  source: string,
  options: ParseOptions,
  statementInfo: StatementInfo,
): DdlClassification | null {
  const start = statementInfo.range.startOffset;
  const end = statementInfo.range.endOffset;
  const i = skipWsCommentsLocal(source, start, end, options);

  const afterCreate = tryConsumeKeyword(source, i, end, options, 'CREATE');
  if (afterCreate !== i) {
    const skipped = consumeModifiers(source, options, afterCreate, end);
    const kindIndex = skipWsCommentsLocal(source, skipped.offset, end, options);
    return classifyAfterCreate(source, options, kindIndex, end, skipped.sawMaterialized);
  }

  const afterAlter = tryConsumeKeyword(source, i, end, options, 'ALTER');
  if (afterAlter !== i) {
    const j = skipWsCommentsLocal(source, afterAlter, end, options);
    return classifyAfterAlter(source, options, j, end);
  }

  const afterDrop = tryConsumeKeyword(source, i, end, options, 'DROP');
  if (afterDrop !== i) {
    return { ddlKind: 'drop', objectKind: 'drop' };
  }

  const afterGrant = tryConsumeKeyword(source, i, end, options, 'GRANT');
  if (afterGrant !== i) {
    return { ddlKind: 'grant', objectKind: 'security' };
  }

  const afterRevoke = tryConsumeKeyword(source, i, end, options, 'REVOKE');
  if (afterRevoke !== i) {
    return { ddlKind: 'revoke', objectKind: 'security' };
  }

  return null;
}

function classifyAfterCreate(
  source: string,
  options: ParseOptions,
  kindIndex: number,
  end: number,
  sawMaterialized: boolean,
): DdlClassification | null {
  for (const [keyword, mapping] of Object.entries(CREATE_KIND_TO_OBJECT_KIND)) {
    const next = tryConsumeKeyword(source, kindIndex, end, options, keyword);
    if (next !== kindIndex) {
      if (sawMaterialized && keyword === 'VIEW') {
        return { ddlKind: 'create-materialized-view', objectKind: 'materialized-view' };
      }
      return mapping;
    }
  }
  return null;
}

function classifyAfterAlter(
  source: string,
  options: ParseOptions,
  kindIndex: number,
  end: number,
): DdlClassification | null {
  // ALTER MATERIALIZED VIEW
  const afterMaterialized = tryConsumeKeyword(source, kindIndex, end, options, 'MATERIALIZED');
  if (afterMaterialized !== kindIndex) {
    const j = skipWsCommentsLocal(source, afterMaterialized, end, options);
    if (tryConsumeKeyword(source, j, end, options, 'VIEW') !== j) {
      return { ddlKind: 'alter-materialized-view', objectKind: 'materialized-view' };
    }
    return null;
  }
  for (const [keyword, mapping] of Object.entries(ALTER_KIND_TO_OBJECT_KIND)) {
    const next = tryConsumeKeyword(source, kindIndex, end, options, keyword);
    if (next !== kindIndex) return mapping;
  }
  return null;
}
